import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { getLibrarySourceTypeLabel, type LibrarySourceType } from "@/lib/library";
import { getSourceTypeIcon } from "@/lib/sourceTypeIcons";

export const ActiveFilterChips = ({
  searchQuery,
  sourceTypeFilters,
  topicFilters,
  tagFilters,
  activeFilterCount,
  onRemoveSearch,
  onToggleSourceType,
  onRemoveTopic,
  onRemoveTag,
  onClearAll,
}: {
  searchQuery: string;
  sourceTypeFilters: LibrarySourceType[];
  topicFilters: string[];
  tagFilters: string[];
  activeFilterCount: number;
  onRemoveSearch: () => void;
  onToggleSourceType: (value: NonNullable<LibrarySourceType>) => void;
  onRemoveTopic: (name: string) => void;
  onRemoveTag: (name: string) => void;
  onClearAll: () => void;
}) => {
  if (!searchQuery && !sourceTypeFilters.length && !topicFilters.length && !tagFilters.length) {
    return null;
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-muted-foreground">Filtered by:</span>
      {searchQuery && (
        <Badge variant="outline" className="border-muted-foreground/40 text-foreground bg-muted gap-1.5">
          <Search className="h-3 w-3" />
          {searchQuery}
          <button
            onClick={onRemoveSearch}
            aria-label={`Remove search filter ${searchQuery}`}
            className="hover:bg-muted-foreground/20 rounded-full p-0.5 -mr-1"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      )}
      {sourceTypeFilters.map((t) => {
        if (!t) return null;
        const Icon = getSourceTypeIcon(t);
        return (
          <Badge
            key={`type-${t}`}
            variant="outline"
            className="gap-1.5 border-primary/40 bg-primary/10 text-foreground"
          >
            <Icon className="h-3 w-3 text-primary" />
            {getLibrarySourceTypeLabel(t)}
            <button
              onClick={() => onToggleSourceType(t as NonNullable<LibrarySourceType>)}
              aria-label={`Remove source type filter ${getLibrarySourceTypeLabel(t)}`}
              className="-mr-1 rounded-full p-0.5 hover:bg-primary/20"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        );
      })}
      {topicFilters.map((name) => (
        <Badge
          key={`topic-${name}`}
          variant="outline"
          className="border-primary text-primary bg-primary/10 gap-1.5"
        >
          {name}
          <button
            onClick={() => onRemoveTopic(name)}
            aria-label={`Remove topic filter ${name}`}
            className="hover:bg-primary/20 rounded-full p-0.5 -mr-1"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      {tagFilters.map((name) => (
        <Badge
          key={`tag-${name}`}
          variant="outline"
          className="border-sage/50 bg-sage/15 text-foreground gap-1.5"
        >
          {name}
          <button
            onClick={() => onRemoveTag(name)}
            aria-label={`Remove tag filter ${name}`}
            className="hover:bg-sage/25 rounded-full p-0.5 -mr-1"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      {activeFilterCount > 1 && (
        <Button variant="ghost" size="sm" onClick={onClearAll} className="h-7 px-2 text-xs">
          Clear all
        </Button>
      )}
    </div>
  );
};
